<template>
  <div class="graph" v-if="region">
    <div class="graph__box">
      <div class="graph__title">
        <img src="@/assets/img/graph-inwoners.svg" />
        {{ filter }}
      </div>

      <div :class="axisClasses">
        <div class="graph__top">
          <template v-if="active === 'rapport'">10</template>
          <template v-if="active === 'position'">1</template>
        </div>
        <div class="graph__middle" v-if="active === 'rapport'">5,5</div>
        <div class="graph__midline" :class="{
          'is-rapport': active === 'rapport'
        }" />
        <div class="graph__bottom">
          <template v-if="active === 'rapport'">0</template>
          <template v-if="active === 'position'">{{ positionEndOfList }}</template>
        </div>
      </div>
      <div class="graph__container">
        <div :class="lineClassnames" v-html="drawSvg()" />
        <template v-for="(value, year) in regionData">
          <div :key="year" class="graph__column">
            <template v-if="value">
              <div :class="holderClassname">
                <div
                  class="graph__score"
                  :class="getClass(value.Rapportcijfer)"
                  :style="{
                    'backgroundColor': value.colors,
                    'top': getTopPosition(value)
                  }"
                >
                  <template v-if="active === 'rapport'">
                    {{ value.Rapportcijfer.toString().replace('.', ',') }}
                  </template>
                  <template v-if="active === 'position'">
                    {{ value.Ranking }}
                  </template>
                </div>
              </div>
            </template>
            <div class="graph__year">{{ year }}</div>
          </div>
        </template>
      </div>
    </div>
    <div class="graph__buttons">
      <button
        class="graph__button"
        :class="{'is-active': active === 'rapport'}"
        @click="active = 'rapport'"
      >Rapportcijfer</button>
      <button
        class="graph__button"
        :class="{'is-active': active === 'position'}"
        @click="active = 'position'"
      >Positie</button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import isMobile from '@/helpers/platform';

export default {
  name: 'SummaryGraph',

  props: {
    region: {
      required: true,
    },

    size: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      states: {
        small: '< 25.000',
        medium: '25.000 - 50.000',
        normal: '50.000 - 100.000',
        large: '> 100.000',
      },
      active: 'rapport',
    };
  },

  computed: {
    ...mapState({
      selectedYear: (state) => state.rawData.year,
    }),

    ...mapGetters({
      data: 'rawData/all',
      sepData: 'rawData/allSepData',
      allYears: 'rawData/allYears',
      statedFilters: 'filter/filters',
    }),

    positionEndOfList() {
      let endNr = 0;

      this.allYears.forEach((year) => {
        const { length } = this.sepData[year][this.size];

        if (length < endNr) {
          return;
        }

        endNr = length;
      });

      return endNr;
    },

    isMobile() {
      return isMobile();
    },

    filter() {
      const items = [];

      Object.keys(this.sepData).forEach((year) => {
        const yearData = this.sepData[year];

        Object.keys(yearData).forEach((group) => {
          const groupData = yearData[group];
          const founded = groupData.filter((item) => item.Gemeente === this.region);

          if (founded.length) {
            items.push(group);
          }
        });
      });

      return this.states[items[0]];
    },

    lineClassnames() {
      const baseClassName = 'graph__lines';
      const classNames = [baseClassName];

      if (this.active === 'position') {
        classNames.push(`${baseClassName}--position`);
      }

      return classNames;
    },

    holderClassname() {
      const classNames = [];

      classNames.push('graph__score-holder');

      return classNames;
    },

    axisClasses() {
      const baseClassName = 'graph__axis';
      const classNames = [baseClassName];

      classNames.push(`${baseClassName}--${this.active}`);

      return classNames;
    },

    regionData() {
      if (!this.region) {
        return null;
      }

      const years = Object.keys(this.data);
      const data = {};

      years.forEach((year) => {
        const item = this.data[year].filter((o) => o.Gemeente === this.region);
        const firstItem = item[0];

        data[year] = firstItem;
      });

      return data;
    },

    coords() {
      if (!this.regionData) {
        return [];
      }

      const years = Object.keys(this.data);

      if (this.active === 'rapport') {
        const coords = [];

        Object.keys(this.regionData).forEach((year, index) => {
          const data = this.regionData[year];

          if (!data) {
            return;
          }

          let left = index === 0 ? 10 : ((190 / (years.length - 1)) * index);

          if (this.isMobile || !this.isMobile) {
            left = index === 0 ? 10 : ((140 / (years.length - 1)) * index);
          }

          coords.push([left, `${100 - (10 * data.Rapportcijfer)}%`]);
        });

        return coords;
      }

      if (this.active === 'position') {
        const coords = [];

        Object.keys(this.regionData).forEach((year, index) => {
          const data = this.regionData[year];

          if (!data) {
            return;
          }

          const length = this.positionEndOfList;
          let left = index === 0 ? 10 : ((190 / (years.length - 1)) * index);

          if (this.isMobile || !this.isMobile) {
            left = index === 0 ? 10 : ((140 / (years.length - 1)) * index);
          }

          coords.push([left, `${(100 / length) * (data.Ranking - 1)}%`]);
        });

        return coords;
      }

      return [];
    },
  },

  methods: {
    getClass(nr) {
      if (this.active !== 'rapport') {
        return '';
      }

      const className = 'graph__score';

      if (nr > 5.5) {
        return `${className}--rapport-high`;
      }

      return `${className}--rapport-low`;
    },

    getTopPosition({ Rapportcijfer, Ranking }) {
      if (this.active === 'rapport') {
        return `${100 - (10 * Rapportcijfer)}%`;
      }

      if (this.active === 'position') {
        const length = this.positionEndOfList;

        return `${(100 / length) * (Ranking - 1)}%`;
      }

      return '';
    },

    drawSvg() {
      if (this.coords.length <= 1) {
        return '';
      }

      let svg = '<svg height="100%" width="100%">';

      this.coords.forEach((_coord, index) => {
        if (index === this.coords.length - 1) {
          return;
        }

        svg += `
          <line
            x1="${this.coords[index][0]}"
            y1="${this.coords[index][1]}"
            x2="${this.coords[index + 1][0]}"
            y2="${this.coords[index + 1][1]}"
          />
        `;
      });

      svg += '</svg>';

      return svg;
    },
  },
};
</script>

<style lang="scss" scoped>
// stylelint-disable
.graph {
  &__box {
    background-color: $color-river-bed;
    border-radius: $global-radius;
    box-shadow: inset 0 10px 19px $color-mirage;
    height: 88px;
    margin-bottom: 15px;
    padding: 25px 0;
    position: relative;

    @include mq($from: mobile) {
      height: 187px;
    }

    &::before,
    &::after {
      background-color: rgba($color-big-stone, .5);
      border-radius: $global-radius;
      content: '';
      height: 25px;
      position: absolute;
      width: 100%;
    }
    &::before {
      left: 0;
      top: 0;
    }
    &::after {
      bottom: 0;
      left: 0;
    }
  }

  &__title {
    @include font-size(micro, 10px);

    align-items: center;
    color: $color-white;
    display: flex;
    left: 50%;
    position: absolute;
    top: 5px;
    transform: translateX(-50%);
    z-index: 1;

    img {
      margin-right: 5px;
    }
  }

  &__axis {
    @include font-size(micro, 10px);

    bottom: 25px;
    color: $color-lynch;
    left: 0;
    position: absolute;
    top: 25px;
    width: 100%;
    z-index: 1;

    .graph__top {
      left: 5px;
      position: absolute;
      top: 5px;
    }

    .graph__middle {
      left: 5px;
      margin-top: -5px;
      position: absolute;
      transform: translateY(-50%);
      top: 50%;
      z-index: 1;
    }

    .graph__bottom {
      bottom: 5px;
      left: 5px;
      position: absolute;
    }
  }

  &__midline {
    background-color: $color-white;
    height: 1px;
    left: 0;
    position: absolute;
    top: 50%;
    width: 100%;

    &.is-rapport {
      margin-top: -5px;
      left: 20px;
      right: 0;
      width: auto;
    }
  }

  &__container {
    display: flex;
    height: 100%;
    justify-content: space-between;
    margin: 0 auto;
    position: relative;
    width: 200px;
    z-index: 1;

    @include mq($from: mobile) {
      width: 150px;
    }
  }

  &__lines {
    bottom: 10px;
    left: 0;
    position: absolute;
    top: 10px;

    ::v-deep svg line {
      stroke: $color-lynch;
      stroke-width: 1;
    }
  }

  &__column {
    @include font-size(micro, 10px);

    color: $color-white;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: flex-end;
    position: relative;
    text-align: center;
    width: 20px;
  }

  &__score-holder {
    flex-grow: 1;
    margin-bottom: 11px;
    margin-top: 11px;
    position: relative;
  }

  &__score {
    align-items: center;
    border-radius: 50%;
    display: flex;
    font-weight: bold;
    height: 20px;
    justify-content: center;
    position: absolute;
    transform: translateY(-50%);
    width: 20px;

    &--rapport-high {
      background-color: $color-conifer !important;
      color: $color-white;
    }

    &--rapport-low {
      background-color: $color-crimson !important;
    }
  }

  &__year {
    bottom: -18px;
    position: absolute;
  }

  &__buttons {
    display: flex;
    justify-content: center;
  }

  &__button {
    @include font-size(theta);

    background-color: $color-lynch;
    border: 0;
    border-radius: $global-radius;
    color: $color-white;
    cursor: pointer;
    font-weight: 500;
    margin-left: 12px;
    min-width: 166px;
    outline: 0;
    padding: 6px 20px;

    &:hover, &.is-active {
      background-color: $color-cerulean;
    }

    &:first-child {
      margin-left: 0;
    }

    @include mq($from: mobile) {
      flex-grow: 1;
      min-width: 0;
      padding-left: 0;
      padding-right: 0;
      width: 50%;
    }
  }
}
</style>
